import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { LegalLayout, type LegalTocItem } from "@/components/legal/LegalLayout";
import { privacyTranslations } from "@/lib/legal/privacyTranslations";
import { isLandingLocale } from "@/lib/landing/locale";

export default function Privacy() {
  const { lang } = useParams<{ lang: string }>();
  // The language comes from the URL (as on the landing page), so the
  // prerendered HTML and the first client render agree.
  const locale = isLandingLocale(lang) ? lang : "cs";
  const t = privacyTranslations[locale];
  const toc: LegalTocItem[] = [
    { id: "controller", label: t.controllerTitle },
    { id: "s1", label: t.s1Title },
    { id: "s2", label: t.s2Title },
    { id: "s3", label: t.s3Title },
    { id: "s4", label: t.s4Title },
    { id: "s5", label: t.s5Title },
    { id: "s6", label: t.s6Title },
    { id: "s7", label: t.s7Title },
    { id: "s8", label: t.s8Title },
    { id: "s9", label: t.s9Title },
    { id: "s10", label: t.s10Title },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <LegalLayout
      locale={locale}
      path="/privacy"
      title={t.title}
      subtitle={t.subtitle}
      effective={t.heroPill}
      toc={toc}
    >
      <div className="space-y-12">
        <section id="controller">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.controllerTitle}
          </h2>
          <p>{t.controllerIntro}</p>
          <div className="my-4 rounded-xl border bg-secondary/40 px-5 py-4 text-sm">
            <p className="font-medium text-foreground">IDEx Corporate s.r.o.</p>
            <p className="mt-1 text-muted-foreground">IČO: 18002161 · {t.vatStatus}</p>
            <p className="text-muted-foreground">Kurzova 2222/16, Stodůlky, 155 00 Praha 5</p>
            <p className="text-muted-foreground">{t.commercialRegister}</p>
            <p className="text-muted-foreground">
              {t.contactLabel}{" "}
              <a
                href="mailto:info@idex-corp.com"
                className="font-medium text-[hsl(var(--action))] underline-offset-2 hover:underline dark:text-primary"
              >
                info@idex-corp.com
              </a>
            </p>
          </div>
        </section>

        <hr className="border-border/70" />

        <section id="s1">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s1Title}
          </h2>
          <p className="mb-2 font-medium text-foreground">{t.s1_1Title}</p>
          <ul className="list-disc space-y-1.5 pl-5 marker:text-[hsl(var(--action))] dark:marker:text-primary">
            {t.s1_1Items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p className="mb-2 mt-4 font-medium text-foreground">{t.s1_2Title}</p>
          <ul className="list-disc space-y-1.5 pl-5 marker:text-[hsl(var(--action))] dark:marker:text-primary">
            {t.s1_2Items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p className="mb-2 mt-4 font-medium text-foreground">{t.s1_3Title}</p>
          <ul className="list-disc space-y-1.5 pl-5 marker:text-[hsl(var(--action))] dark:marker:text-primary">
            {t.s1_3Items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <hr className="border-border/70" />

        <section id="s2">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s2Title}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="pb-2 pr-6 text-left font-medium text-foreground">{t.thPurpose}</th>
                  <th className="pb-2 text-left font-medium text-foreground">{t.thLegalBasis}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {t.purposeRows.map(([ucel, zaklad]) => (
                  <tr key={ucel}>
                    <td className="py-2 pr-6">{ucel}</td>
                    <td className="py-2">{zaklad}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <hr className="border-border/70" />

        <section id="s3">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s3Title}
          </h2>
          <p className="mb-4">{t.s3Intro}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="pb-2 pr-4 text-left font-medium text-foreground">
                    {t.thProcessor}
                  </th>
                  <th className="pb-2 pr-4 text-left font-medium text-foreground">
                    {t.thPurposeCol}
                  </th>
                  <th className="pb-2 text-left font-medium text-foreground">{t.thDataLocation}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {t.processorRows.map(([zprac, ucel, sidlo]) => (
                  <tr key={zprac}>
                    <td className="py-2 pr-4 font-medium text-foreground">{zprac}</td>
                    <td className="py-2 pr-4">{ucel}</td>
                    <td className="py-2">{sidlo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4">{t.s3Footer}</p>
        </section>

        <hr className="border-border/70" />

        <section id="s4">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s4Title}
          </h2>
          <ul className="list-disc space-y-2 pl-5 marker:text-[hsl(var(--action))] dark:marker:text-primary">
            {t.securityItems.map(([label, body]) => (
              <li key={label}>
                <strong className="font-medium text-foreground">{label}</strong> {body}
              </li>
            ))}
          </ul>
        </section>

        <hr className="border-border/70" />

        <section id="s5">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s5Title}
          </h2>
          <ul className="list-disc space-y-1.5 pl-5 marker:text-[hsl(var(--action))] dark:marker:text-primary">
            {t.retentionItems.map(([label, body]) => (
              <li key={label}>
                <strong className="font-medium text-foreground">{label}</strong> {body}
              </li>
            ))}
          </ul>
          <div className="mt-4 rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
            <strong className="font-medium">{t.warnTitle}</strong> {t.warnBody}
          </div>
        </section>

        <hr className="border-border/70" />

        <section id="s6">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s6Title}
          </h2>
          <p className="mb-3">{t.rightsIntro}</p>
          <ul className="list-disc space-y-2 pl-5 marker:text-[hsl(var(--action))] dark:marker:text-primary">
            {t.rightsItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
            <li>
              {t.rightsComplaintBefore}
              <a
                href="https://www.uoou.cz"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-[hsl(var(--action))] underline-offset-2 hover:underline dark:text-primary"
              >
                www.uoou.cz
              </a>
              {t.rightsComplaintAfter}
            </li>
          </ul>
          <p className="mt-4">
            {t.rightsContactBefore}
            <a
              href="mailto:info@idex-corp.com"
              className="font-medium text-[hsl(var(--action))] underline-offset-2 hover:underline dark:text-primary"
            >
              info@idex-corp.com
            </a>
            {t.rightsContactAfter}
          </p>
        </section>

        <hr className="border-border/70" />

        <section id="s7">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s7Title}
          </h2>
          <p>{t.s7p1}</p>
          <p className="mt-3">{t.s7p2}</p>
        </section>

        <hr className="border-border/70" />

        <section id="s8">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s8Title}
          </h2>
          <p>{t.s8p1}</p>
          <p className="mt-3">{t.s8p2}</p>
        </section>

        <hr className="border-border/70" />

        <section id="s9">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s9Title}
          </h2>
          <p>{t.s9p1}</p>
        </section>

        <hr className="border-border/70" />

        <section id="s10">
          <h2 className="mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-[1.375rem]">
            {t.s10Title}
          </h2>
          <div className="rounded-xl border bg-secondary/40 px-5 py-4 text-sm">
            <p className="font-medium text-foreground">IDEx Corporate s.r.o.</p>
            <p className="mt-1 text-muted-foreground">IČO: 18002161</p>
            <p className="text-muted-foreground">Kurzova 2222/16, Stodůlky, 155 00 Praha 5</p>
            <p className="text-muted-foreground">
              {t.emailLabel}{" "}
              <a
                href="mailto:info@idex-corp.com"
                className="font-medium text-[hsl(var(--action))] underline-offset-2 hover:underline dark:text-primary"
              >
                info@idex-corp.com
              </a>
            </p>
          </div>
          <div className="mt-6 text-sm text-muted-foreground">
            <p>{t.closingPlaceDate}</p>
            <p className="mt-1 font-medium text-foreground">IDEx Corporate s.r.o.</p>
          </div>
        </section>
      </div>
    </LegalLayout>
  );
}
